import { useQuery } from '@tanstack/react-query';
import { auth } from '../config/firebase';

export interface ConnectedPage {
  id: string;
  name: string;
  username: string;
  platform: string;
  picture_url: string;
  followers_count: number;
  is_verified: boolean;
  brandId: string;
  parentAccountId: string;
  brandName?: string;
  brandLogo?: string;
  pageId?: string;
}

interface AccountsResponse {
  success: boolean;
  connected_pages: ConnectedPage[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export const useAccounts = () => {
  return useQuery<AccountsResponse>({
    queryKey: ['accounts'],
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    gcTime: 60 * 1000, // Cache persists for 1 minute only (formerly cacheTime)
    refetchOnWindowFocus: true, // Enable refetch when window regains focus
    refetchOnMount: 'always', // Always refetch when component mounts
    retry: 2, // Retry failed requests twice
    queryFn: async () => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Add a cache-busting parameter to ensure we get fresh data
      const cacheBuster = new Date().getTime();
      const response = await fetch(`https://api.socialness.ai/management/accounts?limit=50&offset=0&_=${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }

      const data = await response.json();
      console.log('Accounts API response:', {
        success: data.success,
        pageCount: data.connected_pages?.length,
        pages: data.connected_pages?.map((page: ConnectedPage) => ({
          name: page.name,
          platform: page.platform,
          brandId: page.brandId,
          pageId: page.pageId
        }))
      });
      
      return data;
    }
  });
};